/**
 * Atlas Intelligence > Deals: the acquisition board (Tracking) and the facts Portfolio reads
 * beside the deal book. Served by src/financial/deal-board.ts under /api/financial, behind the
 * same gate as /prospects (operator tier and Partner read; Partner never writes; Sales and
 * every facility role are refused).
 */
import { route } from './route';

const F = '/api/financial';

/** The board's stages, in buying order. Pass and Closed are off the board. */
export type DealStage = 'Sourced' | 'Screening' | 'NDA' | 'LOI' | 'Diligence' | 'IC' | 'Closing' | 'Closed' | 'Pass';
export const OPEN_DEAL_STAGES: readonly DealStage[] = ['Sourced', 'Screening', 'NDA', 'LOI', 'Diligence', 'IC', 'Closing'];
export const PASS_REASONS = ['Price too high', 'Quality risk', 'Seller not ready', 'Better use of capital'] as const;
export type PassReason = (typeof PASS_REASONS)[number];

/** One stage as the server defines it: its exit checks, its assumed odds (MODELED) and its limit. */
export interface DealStageDef {
  key: DealStage;
  gates: string[];
  /** Assumed probability of closing from this stage, until we have our own close history. */
  odds: number;
  limitDays: number;
}

/** One prospect on the board. */
export interface DealBoardRow {
  prospectId: string;
  stage: DealStage;
  stageEnteredAt: string | null;
  /** Exit checks ticked, per stage key. */
  gates: Partial<Record<DealStage, boolean[]>>;
  passReason: string | null;
  passFrom: DealStage | null;
  targetClose: string | null;
  /** Last call, meeting or email logged in the CRM for this facility, or null. */
  lastContactAt: string | null;
  /** Catalog coordinates (geocoded), or null when the facility has none. */
  lat: number | null;
  lng: number | null;
}

/** One logged move or check. */
export interface DealMove {
  at: string;
  who: string;
  what: string;
  kind: 'move' | 'gate' | 'pass' | 'reopen' | 'override';
}

export interface DealBoard {
  stages: DealStageDef[];
  deals: DealBoardRow[];
  moves: DealMove[];
  /** The signed-in person's first name: the owner shown on every card for now. */
  owner: string | null;
}

export interface DealWriteResult {
  ok: true;
  deal: DealBoardRow;
  /** The move-log row this write added. */
  move: DealMove;
  ledgerLabel: string | null;
}

/** GET /prospects/board: every prospect's stage, checks, pass reason and the move log. */
export const intelDealBoard = route<DealBoard>()('GET', `${F}/prospects/board`);
/** POST /prospects/:id/stage: move a deal. `override` moves it past unticked checks (logged). */
export const intelDealStage = route<DealWriteResult, void, { to: DealStage; override?: boolean }>()('POST', `${F}/prospects/:id/stage`);
/** POST /prospects/:id/gate: tick or untick one exit check of a stage. */
export const intelDealGate = route<DealWriteResult, void, { stage: DealStage; index: number; done: boolean }>()('POST', `${F}/prospects/:id/gate`);
/** POST /prospects/:id/pass: take a deal off the board with a reason. */
export const intelDealPass = route<DealWriteResult, void, { reason: PassReason }>()('POST', `${F}/prospects/:id/pass`);
/** POST /prospects/:id/reopen: put a passed deal back at the stage it left. */
export const intelDealReopen = route<DealWriteResult, void, Record<string, never>>()('POST', `${F}/prospects/:id/reopen`);
/** POST /prospects/:id/target-close: the target close date (YYYY-MM-DD), or null to clear it. */
export const intelDealTargetClose = route<DealWriteResult, void, { date: string | null }>()('POST', `${F}/prospects/:id/target-close`);
